<template>
  <ak-card-header class="flex flex--center">
    <h4 class="color-grey mr-auto">Page Analytics</h4>
    <!-- Dropdown for forms -->
    <div v-if="!noApps">
      <ak-select
        v-model="selectedAppName"
        inline-edit
        :options="appsNames"
        placeholder="Select a Landing Page"
        class="min-width-180 max-width-200"
        deselect-label=""
        select-label=""
        selected-label=""
      />
    </div>

    <div v-if="isOwnAccount()">
      <ak-tooltip :placement="'left'" :has-arrow="false">
        <template #trigger>
          <a
            class="icon-action icon-action--inline icon-action--offset icon-cog color-grey-light"
            @click="changeWidget()"
          ></a>
        </template>
        Change Widget
      </ak-tooltip>
    </div>
  </ak-card-header>
  <ak-card-body ref="containerRef" class="h-full flex flex--column overflow-y">
    <!-- Loading section -->
    <ak-loading-spinner v-if="loading" :size="'default'" class="m-auto" />

    <!-- Visits, Leads, Conversion %, Desktop % Mobile % -->
    <div v-if="!loading && !noData && appStats.filteredTotals" class="flex">
      <div class="flex flex--column mr-small">
        <span>Visits</span>
        <h3 class="color-primary">{{ appStats.filteredTotals.totalVisits }}</h3>
      </div>
      <div class="flex flex--column mr-small">
        <span>{{ leadsBranding }}</span>
        <h3 class="color-primary">{{ appStats.filteredTotals.totalLeads }}</h3>
      </div>
      <div class="flex flex--column mr-small">
        <span>Conversion</span>
        <h3 class="color-primary">
          {{
            (
              (appStats.filteredTotals.totalLeads /
                appStats.filteredTotals.totalVisits) *
              100
            ).toFixed(2)
          }}%
        </h3>
      </div>
      <div class="flex flex--column mr-small">
        <span>Desktop Views</span>
        <h3 class="color-secondary">
          {{ getDevicePercentage('false').toFixed(2) }}%
        </h3>
      </div>
      <div class="flex flex--column">
        <span>Mobile Views</span>
        <h3 class="color-secondary">
          {{ getDevicePercentage('true').toFixed(2) }}%
        </h3>
      </div>
    </div>

    <!-- No apps -->
    <ak-empty-state
      v-if="!loading && noData && noApps"
      icon="file-chart-pie"
      fa
    >
      <p>
        Implement {{ !isXEquals() ? 'Akero' : 'X=Convert' }}'s landing pages to
        learn more about your audience and what they're looking for when they
        engage with your advertising
      </p>
      <ak-button color="primary" size="tiny" tag="router-link" :to="newAppLink">
        Create Landing Page
      </ak-button>
    </ak-empty-state>

    <!-- No Data screen -->
    <ak-empty-state
      v-if="!loading && noData && !noApps"
      icon="stats-bars"
      title="No data to display"
    />

    <!-- Graph container -->
    <h4 v-if="!loading && !noData">{{ leadsBranding }} by day</h4>
    <highcharts
      v-if="!loading && !noData"
      :options="chartOptions"
      class="mt-tiny h-full min-h-150"
      style="max-width: 100% !important; min-width: 0 !important"
    ></highcharts>
  </ak-card-body>
</template>

<script>
import CardHeader from '@akerolabs/akero-vue/src/components/cards/CardHeader.vue'
import CardBody from '@akerolabs/akero-vue/src/components/cards/CardBody.vue'
import {
  defineComponent,
  ref,
  computed,
  onMounted,
  watch,
  onUnmounted,
} from 'vue'
import { request } from '@akerolabs/akero-vue/src/utils/request'
import { accountObj, agencyObj } from '@akerolabs/akero-vue/src/store'
import { ToastProgrammatic } from '@akerolabs/akero-vue/src'
import { isOwnAccount } from '@akerolabs/akero-vue/src/services/users'
import { Chart } from 'highcharts-vue'
import { isXEquals } from '@akerolabs/akero-vue/src/services/agency'
import { entityBranding } from '@akerolabs/akero-vue/src/services/entityBranding.js'

export default defineComponent({
  name: 'AkWidgetPageAnalytics',
  components: {
    [CardHeader.name]: CardHeader,
    [CardBody.name]: CardBody,
    highcharts: Chart,
  },
  props: {
    /**
     * @type {Function}
     * @description Update the widget state
     */
    changeWidget: {
      type: Function,
      default: () => ({}),
    },

    /**
     * @type {Function}
     * @description Update the widget state
     */
    setAccountState: {
      type: Function,
      default: () => ({}),
    },

    /**
     * @type {Function}
     * @description Get the widget state
     */
    getAccountState: {
      type: Function,
      default: () => ({}),
    },

    /**
     * @type {String}
     * @description Region/container of the widget (eg. dashRight, dashLeft)
     */
    region: {
      type: String,
      default: '',
    },
  },

  setup(props) {
    const apps = ref([])
    const loading = ref(true)
    const noApps = ref()
    const selectedApp = ref({})
    const appStats = ref({})
    const noData = ref()
    const containerRef = ref(null)
    const formattedData = ref([])

    // Default start date. Updated to app creation date in appSelected()
    const startDate = ref(new Date('2015-01-01T00:00:00.000Z'))
    const endDate = new Date()

    const appsNames = computed(() => apps.value.map((app) => app.title))

    /**
     * @description Account define leads branding
     */
    const leadsBranding = computed(() => {
      return entityBranding('leads.plural', 'Leads')
    })

    /**
     * @type {Object}
     * @description Forms the link object for creating a new app
     */
    const newAppLink = computed(() => ({
      name: 'appNew',
      params: {
        campaignId: null,
        preCampaignId: null,
        typeSlug: 'promo',
      },
    }))

    /**
     * Computed property representing the selected app name.
     *
     * @type {{
     *    get(): string,
     *    set(title: string): void
     * }}
     *
     * @description This computed property handles the selected app name for the widget.
     * The 'get' function returns the title of the currently selected app if it exists, otherwise an empty string.
     * The 'set' function sets the selected app based on the provided 'title'.
     * When setting the 'title', it finds the corresponding app in the 'apps' array and updates 'selectedApp.value'.
     */
    const selectedAppName = computed({
      get: () => (selectedApp.value ? selectedApp.value.title : ''),
      set: (title) => {
        selectedApp.value = apps.value.find((app) => app.title === title)
      },
    })

    /**
     * @type {Object}
     * @description Highcharts options object
     */
    const chartOptions = ref({
      chart: {
        type: 'spline',
        responsive: true,
        events: {
          load: function () {
            var chart = this
            window.addEventListener('resize', () => {
              chart.reflow()
            })
          },
        },
      },
      title: {
        text: '',
      },
      xAxis: {
        type: 'datetime', // Specify datetime type for xAxis
        labels: {
          format: '{value:%Y-%m-%d}', // YYYY-MM-DD
        },
      },
      yAxis: {
        title: {
          text: 'Leads',
        },
      },
      plotOptions: {
        series: {
          color: '#9941f1',
          marker: {
            enabled: false,
          },
        },
      },
      credits: {
        enabled: false,
      },
      loading: {
        style: {
          opacity: 1,
        },
      },
      legend: {
        enabled: false,
      },
      responsive: {
        condition: {
          minHeight: 200,
        },
      },
    })

    /**
     * @param {Object} newVal - new value of selectedApp
     * @returns void
     *
     * @description This watcher is triggered when the selectedApp is changed.
     * If newVal is falsy, it resets the widget state and returns to default values.
     * Otherwise, it sets the widget state and calls appSelected.
     */
    watch(selectedApp, (newVal) => {
      if (!newVal) {
        setDefaultValues() // ensure values are reset
        removeState() // Remove region stored state
        return
      }
      setState(newVal)
      appSelected(newVal)
    })

    /**
     * Fetches the list of apps and updates the 'apps' reactive property with the retrieved data.
     *
     * @async
     *
     * @description Request to retrieve list of apps/landing pages.
     * If the request is successful, it updates the 'apps' reactive property with the retrieved app data.
     * If an error occurs during the request, it shows a Toast notification with the error message.
     * The 'apps' property is updated with an empty array if the request fails.
     */
    const getApps = async () => {
      let appsData = []
      try {
        const { data } = await request.get('/analytics/app', appsRequest.value)
        appsData = data.data
      } catch {
        ToastProgrammatic.open({
          message: 'Error getting page analytics',
          type: 'is-danger',
        })
      }
      apps.value = appsData
    }

    const appsRequest = computed(() => ({
      params: {
        agency: agencyObj()._id,
        account: accountObj()._id,
      },
    }))

    /**
     * Filters and processes the 'appStats' result array based on the selected app.
     *
     * @function
     * @returns {void}
     *
     * @description Filter the 'appStats' result array to include only the data related to the selected app.
     * Stores total leads and total visits from selected app.
     * Sets noData to true if there are no visits
     */
    const filterResultArray = () => {
      if (!selectedApp.value) {
        return [] // Return an empty array if selectedApp is not defined
      }

      const filteredResults = appStats.value.result
        .map((timeFrame) => {
          const filteredValues = timeFrame.value.filter((app) => {
            return app.appId === selectedApp.value._id
          })
          return {
            timeFrame: timeFrame.timeFrame,
            value: filteredValues.length > 0 ? filteredValues : null,
          }
        })
        .filter((timeFrame) => timeFrame.value !== null)

      appStats.value.filteredResults = filteredResults

      // Calculate total leads and total visits
      let totalLeads = 0
      let totalVisits = 0

      filteredResults.forEach((timeFrame) => {
        timeFrame.value.forEach((app) => {
          totalLeads += app.stats.leads
          totalVisits += app.stats.visits
        })
      })
      if (totalVisits === 0) {
        noData.value = true
      } else {
        appStats.value.filteredTotals = {
          totalLeads: totalLeads,
          totalVisits: totalVisits,
        }
      }
    }

    /**
     * Fetches app statistics data and updates 'appStats' with the retrieved data. Then, filters and processes the result array.
     *
     * @async
     * @function
     * @returns {Promise<void>}
     *
     * @description function makes an API request to fetch app statistics.
     * If the request is successful, it updates the 'appStats' with the retrieved app statistics data.
     * If an error occurs during the request, it displays a Toast notification with an error message.
     * The 'filterResultArray' function is called to process the retrieved appStats result data.
     */
    const getAppStats = async () => {
      let appStatsData = {}
      try {
        const { data } = await request.get(
          '/analytics/stats/app',
          appStatsRequest.value,
        )
        appStatsData = data.data
      } catch {
        ToastProgrammatic.open({
          message: 'Error getting page analytics',
          type: 'is-danger',
        })
      }
      appStats.value = appStatsData
      filterResultArray()
    }

    const appStatsRequest = computed(() => ({
      params: {
        agency: agencyObj()._id,
        account: accountObj()._id,
        startDate: startDate.value,
        endDate: endDate,
        interval: 'daily', // Data interval ['daily', 'weekly', 'monthly']
        platforms: ['facebook', 'microsite', 'embed'],
        statuses: ['archived'],
      },
    }))

    /**
     * Fetches app creation dates and updates 'startDate' with the retrieved date.
     *
     * @async
     * @function
     * @returns {Promise<void>}
     *
     * @description This function makes a request to fetch the selected apps creation date.
     * If the request is successful, it updates the 'startDate' property with the first retrieved app creation date.
     * If an error occurs during the request, it displays a Toast notification with an error message.
     * Start date will remain default on error.
     */
    const getAppDates = async () => {
      let appData = {}
      try {
        const { data } = await request.get('/apps', appDatesRequest.value)
        appData = data.data
        startDate.value = new Date(appData[0].createdOn)
        startDate.value.setDate(startDate.value.getDate() - 1)
      } catch {
        ToastProgrammatic.open({
          message: 'Error getting page analytics',
          type: 'is-danger',
        })
      }
    }

    const appDatesRequest = computed(() => ({
      params: {
        _id: selectedApp.value._id,
      },
    }))

    /**
     * Fetches app device statistics and updates 'appStats.value.devices' with the retrieved data.
     *
     * @async
     * @function
     * @returns {Promise<void>}
     *
     * @description This function makes a request to fetch app device statistics.
     * If the request is successful, it updates 'appStats.value.devices' with the retrieved device statistics data.
     * If an error occurs during the request, it displays a Toast notification with an error message.
     */
    const getAppDevices = async () => {
      let appDevicesData = {}
      try {
        const { data } = await request.get(
          '/analytics/stats/device',
          appDevicesRequest.value,
        )
        appDevicesData = data.data
        appStats.value.devices = appDevicesData
      } catch {
        ToastProgrammatic.open({
          message: 'Error getting page analytics',
          type: 'is-danger',
        })
      }
    }

    const appDevicesRequest = computed(() => ({
      params: {
        account: accountObj()._id,
        agency: agencyObj()._id,
        startDate: startDate.value,
        endDate: endDate,
        interval: null,
        campaign: selectedApp.value.campaign,
        app: selectedApp.value._id,
        utm: null,
        platforms: null,
        statuses: null,
      },
    }))

    /**
     * Sets default values for loading, noData, and appStats.
     *
     * @function
     * @returns {void}
     *
     * @description This function sets the following properties to their default values:
     * - 'loading' is set to 'false'
     * - 'noData' is set to 'true'
     * - 'appStats' is set to an empty object
     */
    const setDefaultValues = () => {
      loading.value = false
      noData.value = true
      appStats.value = {}
    }

    /**
     * Sets the state for the widget with the given app ID.
     *
     * @function
     * @param {Object} app - The app object containing the app ID.
     * @returns {void}
     *
     * @description This function sets the state for the widget by updating the 'widgets.data.{region}._id'
     * property with the app ID provided as an argument.
     */
    const setState = (app) => {
      props.setAccountState(`widgets.data.${props.region}._id`, app._id)
    }

    /**
     * Removes the state for the widget and sets default values.
     *
     * @function
     * @returns {void}
     *
     * @description This function removes the state for the widget by updating the 'widgets.data.{region}._id'
     * property to null. Calls 'setDefaultValues'.
     */
    const removeState = () => {
      props.setAccountState(`widgets.data.${props.region}._id`, null)
      setDefaultValues()
    }

    /**
     * Checks and updates the widget state based on the stored app ID.
     *
     * @function
     * @returns {void}
     *
     * @description This function checks if there is a stored app ID in the widget state using 'getAccountState'.
     * If an app ID is found, that app is then selected.
     * If there is no app ID, sets default values.
     */
    const checkForState = () => {
      const desiredApp = props.getAccountState('widgets.data.' + props.region)
      if (!desiredApp || !desiredApp._id || desiredApp._id === '') {
        setDefaultValues() // ensure values are reset
        return
      } else {
        selectedApp.value = apps.value.find((app) => app._id === desiredApp._id)
      }
    }

    /**
     * Builds the data for the Highcharts graph based on the filtered results in 'appStats.value'.
     *
     * @function
     * @returns {void}
     *
     * @description This function constructs the data points for the Highcharts graph based on the filtered results
     * Updates 'chartOptions.value.series' with the formatted data for the 'Leads' series.
     */
    const buildData = () => {
      if (
        !appStats.value.filteredResults ||
        !Array.isArray(appStats.value.filteredResults)
      ) {
        noData.value = true
        return // Handle the case where appStats is not a valid array
      }

      // Create an object to store data points for all time frames
      const dataPoints = {}
      // Loop through appStats and set data points for each time frame
      appStats.value.filteredResults.forEach((item) => {
        const timeFrameStart = new Date(item.timeFrame.start).getTime()
        dataPoints[timeFrameStart] = item.value[0].stats.leads || 0
      })
      // Convert the data points object into an array
      formattedData.value = Object.keys(dataPoints).map((timeFrameStart) => ({
        x: Number(timeFrameStart),
        y: dataPoints[timeFrameStart],
      }))

      // Sort the data array based on the x values (timeFrameStart)
      formattedData.value = formattedData.value.sort((a, b) => a.x - b.x)

      // Limit the data array to 1000 points
      // This is a Highcharts render limitation
      if (formattedData.value.length > 1000) {
        formattedData.value = formattedData.value.slice(-1000)
      }

      chartOptions.value.series = [
        {
          name: 'Leads',
          data: formattedData.value,
        },
      ]
    }

    /**
     * Handles the selection of an app and updates the data for the widget accordingly.
     *
     * @async
     * @function
     * @param {Object} app - The selected app object.
     * @returns {Promise<void>}
     *
     * @description Parent function to handle the selection of an app.
     */
    const appSelected = async (app) => {
      loading.value = true
      selectedApp.value = app
      noData.value = false
      await getAppDates() // Get creation date of app to set start date
      await getAppStats() // Collects and filters app stats
      await getAppDevices() // Collects device stats for app
      buildData()
      loading.value = false
    }

    /**
     * Forces chart to resize when window adjusts
     *
     * @function
     * @returns {void}
     *
     * @description This function is attached to the window resize event listener.
     * It checks if there is data available.
     * Will toggle the loading state for 2 seconds.
     * This forces the Highcharts state to re-render due to V-If
     */
    const onresize = () => {
      if (formattedData.value.length > 0) {
        console.log('resize')

        loading.value = true
        setTimeout(() => {
          loading.value = false
        }, 2000)
      }
    }

    /**
     * Lifecycle hook: Executed when the component is mounted to the DOM.
     *
     * @async
     * @function
     * @returns {Promise<void>}
     *
     * @description This function is executed automatically when the component is mounted to the DOM. It performs the
     * following tasks in sequence:
     * 1. Fetches the list of apps using 'getApps' function.
     * 2. Checks for the presence of a previously selected app state using 'checkForState' function.
     * 3. Updates the 'noApps' flag based on whether there are any apps available.
     * 4. Sets the 'loading' state to false after the necessary data has been fetched and processed.
     */
    onMounted(async () => {
      await getApps()
      checkForState()
      if (apps.value.length > 0) {
        noApps.value = false
      } else {
        noApps.value = true
      }
      window.addEventListener('resize', onresize)
      loading.value = false
    })

    onUnmounted(() => {
      window.removeEventListener('resize', onresize)
    })

    return {
      isOwnAccount,
      loading,
      noData,
      noApps,
      apps,
      appsNames,
      selectedAppName,
      appStats,
      appSelected,
      chartOptions,
      containerRef,
      newAppLink,
      isXEquals,
      leadsBranding,
    }
  },
  computed: {
    /**
     * Computed property: desktopViews
     *
     * @function
     * @returns {number} The number of views from desktop devices.
     *
     * @description This computed property returns the number of views from desktop devices by calling the 'getDeviceViews' method
     * with 'false' as the deviceType parameter.
     */
    desktopViews() {
      return this.getDeviceViews('false')
    },

    /**
     * Computed property: mobileViews
     *
     * @function
     * @returns {number} The number of views from mobile devices.
     *
     * @description This computed property returns the number of views from mobile devices by calling the 'getDeviceViews' method
     * with 'true' as the deviceType parameter.
     */
    mobileViews() {
      return this.getDeviceViews('true')
    },
  },
  methods: {
    /**
     * Method: getDeviceViews
     *
     * @function
     * @param {string} deviceType - The type of device ('true' for mobile, 'false' for desktop).
     * @returns {number} The number of views from the specified device type.
     *
     * @description This method retrieves the number of views from the specified device type (mobile or desktop) from the 'appStats.devices' data.
     * If the data is available, it filters the devices based on the 'deviceType' parameter and returns the corresponding number of views.
     * If the data is not available, it returns 0.
     */
    getDeviceViews(deviceType) {
      if (this.appStats.devices) {
        const device = this.appStats.devices.result.find(
          (item) => item.mobile === deviceType,
        )
        return device ? device.result : 0
      }
      return 0
    },

    /**
     * Method: getDevicePercentage
     *
     * @function
     * @param {string} deviceType - The type of device ('true' for mobile, 'false' for desktop).
     * @returns {number} The percentage of views from the specified device type relative to the total visits.
     *
     * @description This method calculates the percentage of views from the specified device type (mobile or desktop) relative
     * to the total visits available in 'appStats.filteredTotals'.
     * If the total visits are greater than 0, it calculates the percentage and returns the result.
     * If the total visits are 0, it returns 0.
     */
    getDevicePercentage(deviceType) {
      const totalVisits = this.appStats.filteredTotals.totalVisits
      const deviceViews = this.getDeviceViews(deviceType)
      if (totalVisits > 0) {
        return (deviceViews / totalVisits) * 100
      }
      return 0
    },
  },
})
</script>
